import React, { useEffect, useState } from "react";
import { Spinner, Alert, Badge, Button, Modal } from "react-bootstrap";
import API from "../services/api";
import "./VendorRequirementHistory.css";
import { downloadVendorInvoicePdf } from "../utils/pdfDocs";
import RequirementEditForm from "./RequirementEditForm";

const asMapLink = (loc) => {
  try {
    if (!loc) return null;
    // loc can be object or string
    let parsed = typeof loc === "string" ? JSON.parse(loc) : loc;
    const lat = parsed?.lat ?? parsed?.latitude;
    const lng = parsed?.lng ?? parsed?.longitude;
    if (typeof lat === "number" && typeof lng === "number") {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    }
  } catch {}
  return null;
};

const statusBadge = (status) => {
  const s = (status || "").toLowerCase();
  let variant = "secondary";
  if (s === "approved" || s === "in-progress") variant = "info";
  if (s === "completed" || s === "received" || s === "delivered") variant = "success";
  if (s === "returned") variant = "dark";
  if (s === "rejected") variant = "danger";
  if (s === "pending") variant = "warning";
  return <Badge bg={variant}>{status || "N/A"}</Badge>;
};

const requirementAmountOf = (row) =>
  Number(
    row?.requirementAmount ??
      row?.payableAmount ??
      row?.payment?.latestAmountRupees ??
      0
  );

const requirementItemsOf = (row = {}) => {
  if (Array.isArray(row.items) && row.items.length) return row.items;
  return [
    {
      productType: row.productType || "",
      quantity: row.quantity ?? "",
      unit: row.unit || "",
      requirementAmount: row.requirementAmount ?? 0,
    },
  ];
};

const editFlowLabel = (row = {}) => {
  const status = String(row?.editFlow?.status || "none");
  switch (status) {
    case "pending_admin_approval":
      return "Edited by you. Admin approval pending";
    case "vendor_rejected":
      return "You rejected admin edit";
    case "admin_rejected":
      return "Admin rejected your edit";
    case "approved":
      return "Edit approved";
    default:
      return "No pending edit";
  }
};

const resolvePayAmount = (row) => {
  const candidates = [
    row?.livePayable?.totalPayable,
    row?.payment?.latestAmountRupees,
    row?.payableAmount,
    row?.creditTerms?.baseAmount,
    row?.totalAmount,
    row?.amount,
    row?.requirementAmount,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const firstErrorText = (...values) => {
  for (const value of values) {
    if (!value) continue;
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "object") {
      const nested = firstErrorText(
        value.message,
        value.error,
        value.detail,
        value.errorDescription,
        value.responseData?.message,
        value.responseData?.error,
        value.responseData?.detail,
        value.data?.message,
        value.data?.error
      );
      if (nested) return nested;
    }
  }
  return "";
};

const fmtDateIST = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
};

const extractPoNumber = (row = {}) => {
  const direct =
    row?.poNumber ||
    row?.purchaseOrderNo ||
    row?.poNo ||
    row?.dispatch?.purchaseOrderNo ||
    row?.dispatch?.poNumber ||
    "";
  if (String(direct).trim()) return String(direct).trim();

  const noteText = [row?.notes, row?.dispatch?.notes].filter(Boolean).join(" ");
  const match = String(noteText).match(/\bPO(?:\s*(?:No|Number|#))?\s*[:\-]?\s*([A-Z0-9/-]{4,})/i);
  return match?.[1] ? String(match[1]).trim() : "";
};

const buildInvoiceRequirementData = (row = {}, vendorData = {}) => {
  const vendorName = vendorData?.companyName || vendorData?.name || row?.vendor?.companyName || row?.vendor?.name || "";
  const vendorAddress = vendorData?.address || row?.vendor?.address || row?.location?.address || row?.location || "";
  return {
    ...row,
    buyerName: row?.buyerName || row?.customerName || row?.billToName || vendorName,
    buyerAddress: row?.buyerAddress || row?.customerAddress || row?.billToAddress || vendorAddress,
    buyerGST: row?.buyerGST || row?.customerGST || row?.billToGST || vendorData?.gstNumber || "",
    buyerPAN: row?.buyerPAN || row?.customerPAN || row?.billToPAN || vendorData?.panNumber || "",
    poNumber: extractPoNumber(row),
    poDate: row?.poDate || row?.purchaseOrderDate || row?.updatedAt || row?.createdAt || "",
    dueDate: row?.dueDate || row?.creditTerms?.dueDate || "",
    eWayNo: row?.eWayNo || row?.ewayNo || row?.dispatch?.trackingId || "",
    placeOfSupply: row?.placeOfSupply || row?.location?.address || vendorAddress || "",
    shipToName: row?.shipToName || vendorName,
    shipToAddress: row?.shipToAddress || vendorAddress,
    shipToGST: row?.shipToGST || vendorData?.gstNumber || "",
    shipToPAN: row?.shipToPAN || vendorData?.panNumber || "",
    shipState: row?.shipState || row?.state || "",
    buyerState: row?.buyerState || row?.state || "",
  };
};

export default function VendorRequirementHistory() {
  const [items, setItems] = useState([]);
  const [financial, setFinancial] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [payingId, setPayingId] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    draft: {
      productType: "",
      quantity: "",
      unit: "KG",
      requirementAmount: "",
    },
    items: [],
    locationText: "",
    notes: "",
  });

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const txnId = new URLSearchParams(window.location.search).get("merchantTransactionId");
      if (txnId) {
        try {
          await API.get(`/vendor/payments/phonepe/status/${encodeURIComponent(txnId)}`);
        } catch (_e) {
          // continue and show list anyway
        }
      }
      const [historyRes, profileRes] = await Promise.all([
        API.get("/vendor/requirements/history"),
        API.get("/vendor/profile"),
      ]);
      setItems(Array.isArray(historyRes.data) ? historyRes.data : []);
      setVendorProfile(profileRes?.data || null);
      setFinancial(profileRes?.data?.financialSummary || null);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr("Failed to load requirements history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const { data } = await API.get("/vendor/products");
        setProducts(Array.isArray(data) ? data : []);
      } catch {
        setProducts([]);
      }
    };
    loadProducts();
  }, []);

  const paymentBadge = (payment = {}) => {
    if (payment?.isPaid) return <Badge bg="success">Paid</Badge>;
    const s = String(payment?.latestStatus || "unpaid").toLowerCase();
    if (s === "created" || s === "pending") return <Badge bg="warning" text="dark">Pending</Badge>;
    if (s === "failed" || s === "error" || s === "cancelled") return <Badge bg="danger">Failed</Badge>;
    return <Badge bg="secondary">Unpaid</Badge>;
  };

  const handlePayNow = async (reqRow) => {
    try {
      // Pick first valid positive amount from available fields.
      const amount = resolvePayAmount(reqRow);
      if (!Number.isFinite(amount) || amount <= 0) {
        setErr("Is requirement ka payment amount set nahi hai. Admin se amount update karwao.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      setPayingId(reqRow._id);
      setErr("");
      const { data } = await API.post(`/vendor/requirements/${reqRow._id}/payments/phonepe/create`, {
        amount,
        purpose: `Requirement payment: ${reqRow.productType || "Product"}`,
      });
      const payUrl = String(data?.redirectUrl || data?.payUrl || "").trim();
      if (payUrl) {
        window.location.assign(payUrl);
        return;
      }
      setErr("Payment link generate hua but redirect URL missing hai.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const base = e?.response?.data?.message || "Payment start nahi ho paya.";
      const detail = firstErrorText(
        e?.response?.data?.error,
        e?.response?.data?.details,
        e?.response?.data?.error?.responseData,
        e?.response?.data?.error?.message,
        e?.message
      );
      setErr(detail ? `${base}: ${detail}` : base);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setPayingId("");
    }
  };

  const canEditRequirement = (row) => {
    const dispatchStatus = String(row?.dispatch?.status || "pending").toLowerCase();
    const status = String(row?.status || "").toLowerCase();
    return !row?.payment?.isPaid && dispatchStatus === "pending" && status !== "completed";
  };

  const invoiceVendorData = (rowVendor = {}) => {
    const p = vendorProfile || {};
    const bank = p.bank || {};
    return {
      ...rowVendor,
      companyName: p.companyName || rowVendor.companyName || rowVendor.name || "",
      name: p.companyName || rowVendor.name || "",
      gstNumber: p.gstNumber || rowVendor.gstNumber || "",
      panNumber: p.panNumber || rowVendor.panNumber || "",
      address: p.address || p.geoLocation?.address || rowVendor.address || "",
      contact: p.contactMobile || p.whatsappNumber || rowVendor.contact || "",
      email: p.contactEmail || rowVendor.email || "",
      bankName: bank.bankName || rowVendor.bankName || "",
      bankBranch: bank.branch || rowVendor.bankBranch || "",
      bankAccount: bank.accountNumber || rowVendor.bankAccount || "",
      ifsc: bank.ifsc || rowVendor.ifsc || "",
      upiId: p.upiId || rowVendor.upiId || "",
    };
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setEditForm({
      draft: {
        productType: "",
        quantity: "",
        unit: "KG",
        requirementAmount: "",
      },
      items: requirementItemsOf(row).map((it) => ({
        productId: it.productId || "",
        productType: it.productType || "",
        packLabel: it.packLabel || "",
        quantity: it.quantity ?? "",
        unit: it.unit || "KG",
        rate: Number(it.rate || 0),
        subtotalAmount: Number(it.subtotalAmount || 0),
        discountPercent: Number(it.discountPercent || 0),
        discountAmount: Number(it.discountAmount || 0),
        gstPercent: Number(it.gstPercent || 0),
        gstAmount: Number(it.gstAmount || 0),
        totalAmount: Number(it.totalAmount || it.requirementAmount || 0),
        requirementAmount: Number(it.requirementAmount || it.totalAmount || 0),
      })),
      locationText:
        (typeof row?.location === "string" ? row.location : row?.location?.address) ||
        row?.location?.line1 ||
        "",
      notes: row?.notes || "",
    });
  };

  const saveEdit = async (overrideForm = editForm) => {
    if (!editingRow?._id) return;
    if (!Array.isArray(overrideForm.items) || !overrideForm.items.length) return setErr("At least one product item is required");
    if (!String(overrideForm.locationText || "").trim()) return setErr("Location is required");

    try {
      setEditSaving(true);
      setErr("");
      await API.patch(`/vendor/requirements/${editingRow._id}`, {
        items: overrideForm.items,
        notes: overrideForm.notes,
        locationText: String(overrideForm.locationText || "").trim(),
      });
      setEditingRow(null);
      await fetchHistory();
    } catch (e) {
      setErr(e?.response?.data?.message || "Requirement update failed");
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) return <Spinner animation="border" />;

  if (err && !items.length) return <Alert variant="danger">{err}</Alert>;

  if (!items.length) return <Alert variant="info">No requirements submitted yet.</Alert>;

  return (
    <div className="card shadow-sm border-0 vrh-page">
      <div className="card-body vrh-card">
        <h5 className="mb-3">📜 Your Requirement History</h5>
        {financial ? (
          <div className={`vrh-limit-card ${financial.securityDepositPaid ? "ok" : "warn"}`}>
            <div className="vrh-limit-grid">
              <div>
                <div className="small text-muted">Security Deposit</div>
                <div className="fw-semibold">{financial.securityDepositPaid ? "Paid" : "Pending"} • {fmtMoney(financial.securityDepositRequired)}</div>
              </div>
              <div>
                <div className="small text-muted">Approved Limit</div>
                <div className="fw-semibold">{fmtMoney(financial.creditLimit)}</div>
              </div>
              <div>
                <div className="small text-muted">Used</div>
                <div className="fw-semibold">{fmtMoney(financial.utilizedLimit)}</div>
              </div>
              <div>
                <div className="small text-muted">Remaining Limit</div>
                <div className="fw-semibold">{fmtMoney(financial.availableLimit)}</div>
              </div>
            </div>
            <div className="small mt-2 text-muted">
              Payment schedule: {Number(financial.paymentDueDays || 0)} days • Delay penalty {Number(financial.dailyPenaltyPercent || 0).toFixed(2)}% per day
            </div>
          </div>
        ) : null}
        {err ? <Alert variant="danger">{err}</Alert> : null}
        <div className="vrh-cards-list">
          {items.map((r) => {
            const mapLink = asMapLink(r.location);
            const dueDateText = fmtDateIST(r?.creditTerms?.dueDate);
            const overdueDays = Number(r?.livePayable?.overdueDays || 0);
            const penaltyAmount = Number(r?.livePayable?.penaltyAmount || 0);
            const dt = r.createdAt
              ? new Date(r.createdAt).toLocaleString("en-IN", {
                  timeZone: "Asia/Kolkata",
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })
              : "-";
            return (
              <div className="vrh-mobile-card" key={r._id}>
                {(() => {
                  const reqAmount = requirementAmountOf(r);
                  return (
                    <div className="vrh-row">
                      <span>Amount</span>
                      <span>
                        {reqAmount > 0 ? `₹ ${reqAmount.toLocaleString("en-IN")}` : "-"}
                        {Number(r.gstAmount || 0) > 0 ? ` (GST ₹ ${Number(r.gstAmount || 0).toLocaleString("en-IN")})` : ""}
                      </span>
                    </div>
                  );
                })()}
                <div className="vrh-head">
                  <strong>{r.productType || "-"}</strong>
                  {statusBadge(r.status)}
                </div>
                <div className="text-muted small">{dt}</div>
                <div className="vrh-items-list">
                  {requirementItemsOf(r).map((it, idx) => (
                    <div key={`${r._id}_${idx}`} className="vrh-item-line">
                      {it.packLabel || it.productType || "-"} | {it.quantity ?? "-"} {it.unit || "-"} | ₹ {Number(it.requirementAmount || it.totalAmount || 0).toLocaleString("en-IN")}
                      {Number(it.gstPercent || 0) > 0 ? ` | GST ${Number(it.gstPercent).toLocaleString("en-IN")} %` : ""}
                    </div>
                  ))}
                </div>
                <div className="vrh-row"><span>Approval</span><span>{statusBadge(r.approvalStatus)}</span></div>
                <div className="vrh-row"><span>Edit Status</span><span>{editFlowLabel(r)}</span></div>
                <div className="vrh-row">
                  <span>Dispatch</span>
                  <span>
                    {statusBadge(r.dispatch?.status || "pending")}
                    {r.dispatch?.trackingId ? (
                      <div className="small text-muted">#{r.dispatch.trackingId}</div>
                    ) : null}
                  </span>
                </div>
                <div className="vrh-row">
                  <span>Assigned</span>
                  <span>
                    {r.assignedEmployee
                      ? r.assignedEmployee.fullName || r.assignedEmployee.email || r.assignedEmployee.mobile
                      : "-"}
                  </span>
                </div>
                <div className="vrh-row">
                  <span>Location</span>
                  <span>
                    {mapLink ? <a href={mapLink} target="_blank" rel="noreferrer">View Map</a> : "N/A"}
                  </span>
                </div>
                <div className="vrh-row"><span>Due Date</span><span>{dueDateText}</span></div>
                <div className="vrh-row"><span>Overdue</span><span>{r.payment?.isPaid ? "Cleared" : `${overdueDays} days`}</span></div>
                <div className="vrh-row"><span>Penalty</span><span>{fmtMoney(penaltyAmount)}</span></div>
                <div className="vrh-footer">
                  <div>
                    {paymentBadge(r.payment)}
                    <div className="small text-muted mt-1">
                      {fmtMoney(Number(r?.livePayable?.totalPayable || requirementAmountOf(r)))}
                    </div>
                  </div>
                  <div>
                    {canEditRequirement(r) ? (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="me-2"
                        onClick={() => openEdit(r)}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline-secondary" className="me-2" disabled>
                        Edit Locked
                      </Button>
                    )}
                    {r.payment?.isPaid ? (
                      <span className="text-success small fw-semibold">Payment Done</span>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        disabled={payingId === r._id || resolvePayAmount(r) <= 0}
                        onClick={() => handlePayNow(r)}
                      >
                        {payingId === r._id ? "Starting..." : "Pay Now"}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline-dark"
                      className="ms-2"
                      onClick={() => {
                        const vendorData = invoiceVendorData(r.vendor || {});
                        void downloadVendorInvoicePdf({
                          requirement: buildInvoiceRequirementData(r, vendorData),
                          vendor: vendorData,
                        });
                      }}
                    >
                      Invoice PDF
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Modal show={!!editingRow} onHide={() => setEditingRow(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Requirement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <RequirementEditForm
            products={products}
            form={editForm}
            setForm={setEditForm}
            saving={editSaving}
            onSubmit={saveEdit}
            submitLabel="Send To Admin"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingRow(null)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
